import { memo, useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Download, GitCompare, RefreshCw } from 'lucide-react'
import type { MaintenanceJob, UpdateAvailability, UpdateSafety } from '../../types/index.js'
import { maintenanceApi } from '../../lib/maintenanceApi.js'
import { maintenanceJobStalled } from '../../lib/maintenanceJobStall.js'
import { useAuthUser } from '../../store/index.js'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert.js'
import { Badge } from '../ui/badge.js'
import { Button } from '../ui/button.js'
import { Checkbox } from '../ui/checkbox.js'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog.js'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '../ui/field.js'
import { Input } from '../ui/input.js'
import { Progress } from '../ui/progress.js'
import { Spinner } from '../common/Spinner.js'

type GitResult = { branch: string; localCommit: string; remoteCommit: string; clean: boolean; ahead: number; behind: number; divergent: boolean; summary: string; date: string }

export const UpdateControl = memo(function UpdateControl() {
  const user = useAuthUser()
  const [open, setOpen] = useState(false)
  const [availability, setAvailability] = useState<UpdateAvailability | null>(null)
  const [git, setGit] = useState<GitResult | null>(null)
  const [job, setJob] = useState<MaintenanceJob | null>(null)
  const [password, setPassword] = useState('')
  const [totp, setTotp] = useState('')
  const [acceptOnAir, setAcceptOnAir] = useState(false)
  const [error, setError] = useState('')

  const follow = useCallback(async (id: string) => {
    try {
      const next = await maintenanceApi.job(id); setJob(next)
      if (next.state === 'SUCCEEDED') {
        if (next.type === 'GIT_STATUS') setGit(next.result as unknown as GitResult)
        return
      }
      if (next.state === 'FAILED') { setError(next.error?.message ?? 'La tâche a échoué.'); return }
      if (maintenanceJobStalled(next)) { setError('Le contrôleur de maintenance ne prend pas la demande en charge. Vérifiez le service trafficplay-maintenance sur le serveur.'); return }
      window.setTimeout(() => void follow(id), 1_500)
    } catch { window.setTimeout(() => void follow(id), 3_000) }
  }, [])
  const refresh = useCallback(async () => {
    if (user?.role !== 'ADMIN') return
    setError('')
    try { const next = await maintenanceApi.update(); setAvailability(next); if (next.jobId) void follow(next.jobId) }
    catch { setAvailability(null); setError('Le contrôleur de maintenance est hors ligne.') }
  }, [follow, user?.role])
  useEffect(() => {
    void refresh()
    const timer = window.setInterval(() => void refresh(), 30 * 60 * 1_000)
    return () => window.clearInterval(timer)
  }, [refresh])

  const start = useCallback(async () => {
    if (!git) return
    setError('')
    try {
      const next = await maintenanceApi.startUpdate({ targetCommit: git.remoteCommit, password, totp, acceptOnAir })
      setJob(next); setPassword(''); setTotp(''); void follow(next.id)
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'Mise à jour impossible.') }
  }, [acceptOnAir, follow, git, password, totp])

  if (user?.role !== 'ADMIN') return null
  const available = Boolean(git && git.behind > 0 && git.clean && git.branch === 'main' && !git.divergent)
  const safety = availability?.safety
  return <Dialog open={open} onOpenChange={setOpen}>
    <DialogTrigger asChild><Button type="button" variant="ghost" size="sm" className="tp-update-trigger"><Download data-icon="inline-start" />Mise à jour{available && <Badge variant="secondary">{git?.behind}</Badge>}</Button></DialogTrigger>
    <DialogContent className="tp-form-surface maintenance-update-dialog sm:max-w-2xl">
      <DialogHeader><DialogTitle>Mise à jour de TrafficPlay</DialogTitle><DialogDescription>Vérifiez le commit cible et l’état de toutes les chaînes avant publication.</DialogDescription></DialogHeader>
      {error && <Alert variant="destructive"><AlertTriangle aria-hidden="true" /><AlertTitle>Action requise</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}
      {!git ? <div className="maintenance-loading"><Spinner />Vérification de origin/main…</div> : <GitSummary git={git} />}
      {safety && !safety.safe && <SafetyWarning safety={safety} accepted={acceptOnAir} onAccepted={setAcceptOnAir} />}
      {job && job.type === 'UPDATE' && <div className="maintenance-job" role="status"><Progress value={job.progress} /><span>{job.stage ?? job.state} · {job.progress} %</span>{job.error?.recovery && <p>{job.error.recovery}</p>}</div>}
      {git && available && (!job || ['SUCCEEDED', 'FAILED', 'CANCELLED'].includes(job.state)) && <FieldGroup><Field><FieldLabel htmlFor="update-password">Mot de passe administrateur</FieldLabel><Input id="update-password" type="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} /></Field><Field><FieldLabel htmlFor="update-totp">Code TOTP</FieldLabel><Input id="update-totp" inputMode="numeric" autoComplete="one-time-code" value={totp} onChange={event => setTotp(event.target.value)} /><FieldDescription>Le code sert uniquement à cette opération.</FieldDescription></Field></FieldGroup>}
      <DialogFooter><Button type="button" variant="outline" onClick={() => void refresh()}><RefreshCw data-icon="inline-start" />Actualiser</Button>{git && available && <Button type="button" disabled={!password || totp.length < 6 || Boolean(safety && !safety.safe && !acceptOnAir)} onClick={() => void start()}>Installer {git.remoteCommit.slice(0, 8)}</Button>}</DialogFooter>
    </DialogContent>
  </Dialog>
})

function GitSummary({ git }: { git: GitResult }) {
  return <div className="maintenance-git"><div><span>Local</span><code>{git.localCommit.slice(0, 12)}</code></div><GitCompare aria-hidden="true" /><div><span>origin/main</span><code>{git.remoteCommit.slice(0, 12)}</code></div><p>{git.summary} · {new Date(git.date).toLocaleString('fr-FR')}</p><p>Branche {git.branch} · {git.clean ? 'dépôt propre' : 'modifications locales'} · {git.behind} commit(s) en retard</p></div>
}

function SafetyWarning({ safety, accepted, onAccepted }: { safety: UpdateSafety; accepted: boolean; onAccepted: (value: boolean) => void }) {
  return <Alert variant="destructive"><AlertTriangle aria-hidden="true" /><AlertTitle>Contenu antenne bloquant détecté</AlertTitle><AlertDescription><ul>{safety.blockers.map(blocker => <li key={blocker.itemId}>{blocker.channelName} · {blocker.title} ({blocker.itemType}) · {new Date(blocker.startTime).toLocaleTimeString('fr-FR')} – {new Date(blocker.endTime).toLocaleTimeString('fr-FR')}</li>)}</ul><label className="maintenance-consent"><Checkbox checked={accepted} onCheckedChange={value => onAccepted(value === true)} />J’autorise explicitement la bascule à chaud avec rollback vérifié.</label></AlertDescription></Alert>
}
